/**
 * CocktailGrid — centered 2×2 grid of signature cocktails on the guest Home
 * page. Tapping a card opens the shared DetailModal with the full cocktail
 * story. Informational only — the venue handles drinks.
 */
import { useState } from "react";
import { Wine } from "lucide-react";
import DetailModal from "./DetailModal";
import { T } from "../lib/i18n";
import { buildCocktailList, recommendationTone } from "../data/cocktailData";

const ALC_TONE = "#b45309";
const NONALC_TONE = "#0d9488";

export default function CocktailGrid({ cocktails }) {
  const [selected, setSelected] = useState(null);
  const list = buildCocktailList(cocktails);

  return (
    <div className="card-elev p-4" data-testid="cocktail-grid">
      <div className="flex items-center gap-2 mb-3">
        <Wine className="w-3.5 h-3.5 text-primary-motif" strokeWidth={1.5} />
        <div className="text-[10px] tracking-luxe text-motif-muted">{T("signature cocktails tonight")}</div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {list.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => setSelected(c)}
            className="rounded-xl overflow-hidden bg-accent-motif text-left transition hover:shadow-md active:opacity-80 hover:-translate-y-[1px]"
            data-testid={`cocktail-card-${c.key}`}
            aria-label={`Open details for ${c.name}`}
          >
            <CocktailThumb c={c} />
            <div className="p-2.5">
              <div className="font-display text-[15px] text-motif leading-tight">{c.name}</div>
              <span
                className="inline-block mt-1.5 text-[9px] px-1.5 py-0.5 rounded-full font-medium leading-none tracking-wide"
                style={{
                  background: `${recommendationTone(c.recommendation)}15`,
                  color: recommendationTone(c.recommendation),
                  border: `1px solid ${recommendationTone(c.recommendation)}50`,
                }}
              >
                {T(c.recommendation.replace(" Recommendation", "'s Pick"))}
              </span>
            </div>
          </button>
        ))}
      </div>
      <div className="text-[10px] text-motif-muted mt-3 italic text-center">
        {T("Please order drinks at the bar.")}
      </div>

      {selected && (
        <DetailModal
          onClose={() => setSelected(null)}
          image={selected.image}
          imageAlt={selected.name}
          imageFit={selected.display?.fit || "contain"}
          imagePosition={selected.display?.position || "center"}
          imageBg={selected.display?.bg || null}
          fallback={{ grad: ["#f3e8d7", "#d4b280"], emoji: "🍸" }}
          eyebrow={T("Signature Cocktail")}
          title={selected.name}
          recommendation={{ label: selected.recommendation, tone: recommendationTone(selected.recommendation) }}
          badges={[
            selected.alcoholic
              ? { key: "alc", label: T("Alcoholic"), tone: ALC_TONE }
              : { key: "nonalc", label: T("Non-Alcoholic"), tone: NONALC_TONE },
          ]}
          description={selected.description}
          flavor={selected.flavor}
          ingredients={selected.ingredients}
          testIdPrefix="cocktail-modal"
        />
      )}
    </div>
  );
}

function CocktailThumb({ c }) {
  const [failed, setFailed] = useState(false);
  if (c.image && !failed) {
    return (
      <img
        src={c.image}
        alt={c.name}
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
        className="w-full aspect-square object-cover"
      />
    );
  }
  return (
    <div
      className="w-full aspect-square flex items-center justify-center"
      style={{ background: "linear-gradient(135deg, #f3e8d7, #d4b280)" }}
    >
      <span className="text-4xl" role="img" aria-hidden="true">🍸</span>
    </div>
  );
}
